import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import { useColorScheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

import { useJsrClient } from 'src/hooks/use-jsr-client';

import { logger } from 'src/utils/logger';

import { updateCurrentUser } from 'src/actions/user/me';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings/context';
import { BaseOption } from 'src/components/settings/drawer/base-option';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export function SystemSettings({ open, onClose }) {
    const { mode, setMode } = useColorScheme();
    const { user, checkUserSession } = useAuthContext();
    const { getDashboards, isJsrAvailable } = useJsrClient();
    const settings = useSettingsContext();

    const [dashboards, setDashboards] = useState([]);
    const [loadingDashboards, setLoadingDashboards] = useState(false);
    const [saving, setSaving] = useState(false);

    const { control, handleSubmit, setValue, reset } = useForm({
        defaultValues: {
            dashboard: '',
        },
    });

    // Load dashboards when dialog opens
    useEffect(() => {
        if (!open) {
            return;
        }
        if (isJsrAvailable) {
            loadDashboards();
        }
    }, [open, isJsrAvailable]);

    // Load current user settings when dialog opens
    useEffect(() => {
        if (!open) {
            return;
        }
        if (user) {
            setValue('dashboard', user.homeDashboard || '');
        }
    }, [open, user, setValue]);

    const loadDashboards = async () => {
        try {
            setLoadingDashboards(true);
            const data = await getDashboards();
            setDashboards(data || []);
        } catch (error) {
            logger.error('Failed to load dashboards:', error);
            setDashboards([]);
        } finally {
            setLoadingDashboards(false);
        }
    };

    const onSubmit = async (data) => {
        try {
            setSaving(true);

            // Update home_dashboard in backend
            await updateCurrentUser({
                homeDashboard: data.dashboard || null,
            });

            // Refresh user session to get updated data
            await checkUserSession?.();

            toast.success('Settings saved successfully');

            onClose();
        } catch (error) {
            logger.error('Failed to save settings:', error);
            toast.error('Failed to save settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset form to current values
        reset({
            dashboard: user?.homeDashboard || '',
        });
        onClose();
    };

    const handleDarkModeToggle = () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        settings.onUpdateField('colorScheme', newMode);
        setMode(newMode);
    };

    const handleCompactToggle = () => {
        settings.onUpdateField('compactLayout', !settings.compactLayout);
    };

    return (
        <Drawer
            open={open}
            onClose={handleCancel}
            anchor="right"
            slotProps={{ backdrop: { invisible: true } }}
            PaperProps={{ sx: { width: 400 } }}
        >
            <IconButton
                onClick={handleCancel}
                sx={{ top: 12, left: 12, zIndex: 9, position: 'absolute' }}
            >
                <Iconify icon="mingcute:close-line" />
            </IconButton>

            <Scrollbar>
                <Stack sx={{ pt: 8, px: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        System Settings
                    </Typography>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={3}>
                            {/* Dashboard Selection */}
                            <FormControl fullWidth disabled={!isJsrAvailable}>
                                <InputLabel id="dashboard-select-label">Default Dashboard</InputLabel>
                                <Controller
                                    name="dashboard"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            labelId="dashboard-select-label"
                                            label="Default Dashboard"
                                            disabled={loadingDashboards || !isJsrAvailable}
                                            startAdornment={
                                                loadingDashboards && (
                                                    <Box sx={{ display: 'flex', mr: 1 }}>
                                                        <CircularProgress size={20} />
                                                    </Box>
                                                )
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {dashboards.map((dashboard) => (
                                                <MenuItem key={dashboard.uri} value={dashboard.uri}>
                                                    {dashboard.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>

                            {!isJsrAvailable && (
                                <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                    Dashboard API is not available.
                                </Box>
                            )}

                            <Typography variant="h6">
                                Layout
                            </Typography>
                            {/* Theme and Layout Options */}
                            <Box gap={2} display="grid" gridTemplateColumns="repeat(2, 1fr)">
                                <BaseOption
                                    label="Dark mode"
                                    icon="moon"
                                    selected={settings.colorScheme === 'dark'}
                                    onClick={handleDarkModeToggle}
                                />
                                <BaseOption
                                    tooltip="Dashboard only and available at large resolutions > 1600px (xl)"
                                    label="Compact"
                                    icon="autofit-width"
                                    selected={settings.compactLayout}
                                    onClick={handleCompactToggle}
                                />
                            </Box>

                        </Stack>
                    </form>
                </Stack>
            </Scrollbar>

            <Box sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        onClick={handleCancel}
                        color="inherit"
                        disabled={saving}
                        fullWidth
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        variant="contained"
                        disabled={saving || loadingDashboards}
                        loading={saving}
                        fullWidth
                    >
                        Save
                    </Button>
                </Stack>
            </Box>
        </Drawer>
    );
}
